import { useMemo } from 'react';
import ReactDragListView from 'react-drag-listview';
import { observer } from 'mobx-react';
import { find } from 'lodash';
import { theme, Alert } from 'antd';
import { t } from 'i18next';

import { useStores } from '@/hooks/use-stores';
import CloseIcon from '@/assets/icon-close.svg?react';
import PlusIcon from '@/comp/common/icons/PlusIcon';
import DragAndDropIcon from '@/comp/common/icons/DragAndDropIcon';
import { EngineStepsEnum } from '@/store/Engine/constants';

import { DragLineGlobalStyle, ListContainer, SelectCompressor } from './styled';
import { CompressorsGroup } from '@/store/Engine/Compressors';
import { EngineStore } from '@/store/Engine/EngineStore';
import DevicesStore from '@/store/Devices/DevicesStore';

const { useToken } = theme;

const DragAndDropList = observer(
  ({
    compressorGroupId,
    hasTriedToSubmit,
    compressorsGroup,
  }: {
    compressorGroupId: string;
    hasTriedToSubmit: boolean;
    compressorsGroup: CompressorsGroup;
  }) => {
    const { engineStore, devicesStore }: { engineStore?: EngineStore; devicesStore?: DevicesStore } = useStores();
    const { compressorGroups, validationMessages } = engineStore;
    const data = find(compressorGroups?.compressorsGroups, { id: compressorGroupId })?.compressors ?? [];
    const { token } = useToken();

    const onDragEnd = (fromIndex, toIndex) => {
      if (toIndex < 0) return; // Ignores if outside designated area

      const items = [...data];
      const item = items.splice(fromIndex, 1)[0];
      items.splice(toIndex, 0, item);
      compressorGroups?.setCompressorGroupField(compressorGroupId, 'compressors', items);
    };

    const onGroupFieldChange = (compressorGroupId, field) => e => {
      compressorGroups?.setCompressorGroupField(compressorGroupId, field, e?.target?.value ?? e);
      if (hasTriedToSubmit) {
        engineStore.validate(EngineStepsEnum.CompressorGroups, null, null, `${field}-${compressorGroupId}`);
      }
    };

    const onDeleteCompressor = (compressorGroupId, compressorId) => () =>
      onGroupFieldChange(
        compressorGroupId,
        'compressors',
      )(
        find(compressorGroups?.compressorsGroups, { id: compressorGroupId })?.compressors?.filter(
          compressor => compressor !== compressorId,
        ),
      );

    const compressorsOptions = useMemo(
      () =>
        engineStore.applicableCompressorsForEngine?.reduce(
          (result, current) => [
            ...result,
            ...(compressorGroups?.isCompressorInAnyGroup(current?.id)
              ? []
              : [{ label: current?.name, value: current?.id }]),
          ],
          [],
        ),
      [compressorGroups?.compressorsGroups, engineStore.applicableCompressorsForEngine],
    );

    const validationError =
      hasTriedToSubmit &&
      find(validationMessages, { key: compressorsGroup?.id, field: 'compressors', focused: true })?.message;

    return (
      <>
        <DragLineGlobalStyle />
        <ReactDragListView
          nodeSelector=".list-item"
          handleSelector=".draggable-icon-stacked"
          lineClassName="drag-line"
          onDragEnd={onDragEnd}
        >
          <ListContainer>
            {data?.map(compressor => (
              <div className="list-item" key={compressor}>
                <DragAndDropIcon className="draggable-icon-stacked" />
                <span className="list-item-title">
                  {find(devicesStore.compressors, { id: compressor })?.name ||
                    find(engineStore.pidChanges?.compressorsRemoved, { deviceId: compressor })?.deviceName}
                </span>
                <CloseIcon
                  color={token.colorBorder}
                  className="icon-close"
                  onClick={onDeleteCompressor(compressorsGroup?.id, compressor)}
                />
              </div>
            ))}

            <SelectCompressor
              mode="multiple"
              value={compressorsGroup?.compressors?.map(compressor => ({
                value: compressor,
                label: find(devicesStore.compressors, { id: compressor })?.name,
              }))}
              options={compressorsOptions}
              onChange={onGroupFieldChange(compressorsGroup?.id, 'compressors')}
              popupClassName={'dropdown-style'}
              suffixIcon={<PlusIcon color={token.colorBorder} />}
              filterOption={(input, option) =>
                option?.label && option.label?.toLowerCase()?.includes(input?.toLocaleLowerCase())
              }
            />
            {validationError && (
              <Alert message={t(validationError)} type="error" showIcon className="validation-error-alert" />
            )}
          </ListContainer>
        </ReactDragListView>
      </>
    );
  },
);

export default DragAndDropList;
